import { create } from 'zustand'
import { apiClient, setAccessToken, setUnauthenticatedHandler } from '@/lib/api'
import { can, type PermissionValue } from '@/lib/permissions'
import { queryClient } from '@/lib/queryClient'

export interface Session {
  accessToken: string
  expiresAtUtc: string
  storeCode: string
  storeName: string
  userId: number
  displayName: string
  username: string
  role: 'Cashier' | 'Manager' | 'Admin'
  permissions: number
  branchId: number | null
  mustChangePassword: boolean
  /** When the shop's license runs out. Null for one with no end date. */
  licenseExpiresAtUtc: string | null
}

interface SessionState {
  session: Session | null
  /** True until the initial silent refresh has settled, so the shell can hold the route. */
  isRestoring: boolean
  login: (storeCode: string, login: string, password: string) => Promise<void>
  logout: () => Promise<void>
  restore: () => Promise<void>
  can: (permission: PermissionValue) => boolean
}

/** Names the till in the session list, so an owner can tell devices apart. */
function deviceLabel(): string {
  const isMobile = /android|iphone|ipad|mobile/i.test(navigator.userAgent)
  return isMobile ? 'Mobile till' : 'Desktop till'
}

export const useSession = create<SessionState>((set, get) => ({
  session: null,
  isRestoring: true,

  login: async (storeCode, login, password) => {
    const session = await apiClient.post<Session>('/api/auth/login', {
      storeCode: storeCode.trim().toUpperCase(),
      login: login.trim(),
      password,
      deviceLabel: deviceLabel(),
    })

    setAccessToken(session.accessToken)
    set({ session, isRestoring: false })
  },

  logout: async () => {
    try {
      await apiClient.post('/api/auth/logout')
    } finally {
      // Clear locally even if the call failed — the cashier asked to be signed out, and the
      // server-side token expires on its own.
      setAccessToken(null)
      set({ session: null })
      // Without this, whoever logs in next — the same cashier moments later, or a different
      // one on a shared till — starts from cached answers this session left behind: an old
      // "no shift open" for a shift that has since opened, a stale product list, another
      // cashier's sales history. A hard refresh happens to wipe this too, which is why the
      // symptom looks like it needs one; the actual fix is not depending on that.
      queryClient.clear()
    }
  },

  restore: async () => {
    try {
      // Succeeds only if the httpOnly refresh cookie is still valid, which is what makes a
      // page reload keep the cashier signed in without any token in localStorage.
      const session = await apiClient.post<Session>('/api/auth/refresh')
      setAccessToken(session.accessToken)
      set({ session })
    } catch {
      set({ session: null })
    } finally {
      set({ isRestoring: false })
    }
  },

  can: (permission) => {
    const { session } = get()
    return session ? can(session.permissions, permission) : false
  },
}))

// When a refresh finally fails, drop the session so the router sends the user to sign in.
// Same reasoning as logout() — whoever signs in next must not inherit this session's cache.
setUnauthenticatedHandler(() => {
  useSession.setState({ session: null, isRestoring: false })
  queryClient.clear()
})
